import React, { useEffect, useRef, useState } from "react";
import {
    Sparkles,
    Send,
    Loader2,
    Trash2,
    User as UserIcon,
    Bot,
    HelpCircle,
    FileDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, chatWithTender, clearTenderChat } from "@/lib/api";

const SUGGESTIONS = [
    "¿Cuáles son los criterios de adjudicación y sus pesos?",
    "¿Hay personal a subrogar? ¿Cuántos trabajadores?",
    "¿Qué penalidades hay por retraso o incumplimiento?",
    "¿Qué documentos exactos van en cada sobre?",
    "Calcula el coste mínimo del contrato según el pliego",
    "¿Qué solvencia económica y técnica se exige?",
    "¿Cuáles son las mejoras valorables y cuánto puntúan?",
    "Dame el calendario de fechas críticas",
    "¿Qué riesgos identificas en este pliego?",
];

/**
 * TenderChat — large, very visible chat panel for asking anything about the
 * pliego. Conversation history persists on the tender doc.
 *
 * Props:
 *   tender — the tender object (with chat_messages)
 *   onUpdated(updatedTender) — called after each turn so the parent refreshes
 */
export function TenderChat({ tender, onUpdated }) {
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const messagesEnd = useRef(null);
    const messages = tender?.chat_messages || [];

    useEffect(() => {
        // Auto-scroll on new message
        messagesEnd.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages.length, sending]);

    const send = async (overrideText) => {
        const text = (overrideText ?? input).trim();
        if (!text || sending) return;
        setSending(true);
        try {
            const updated = await chatWithTender(tender.id, text);
            onUpdated?.(updated);
            setInput("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo enviar la pregunta");
        } finally {
            setSending(false);
        }
    };

    const clear = async () => {
        if (!messages.length) return;
        if (!window.confirm("¿Borrar toda la conversación de este pliego?")) return;
        try {
            const updated = await clearTenderChat(tender.id);
            onUpdated?.(updated);
            toast.success("Conversación borrada");
        } catch {
            toast.error("No se pudo borrar la conversación");
        }
    };

    const exportPdf = async () => {
        if (!messages.length) return;
        try {
            const res = await api.get(`/tenders/${tender.id}/chat/export-pdf`, {
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `brief-${(tender.title || "licitacion").slice(0, 60)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Brief exportado a PDF");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo exportar la conversación"
            );
        }
    };

    const onKey = (e) => {
        // Enter to send, Shift+Enter for newline
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const empty = messages.length === 0;

    return (
        <div
            className="relative bg-gradient-to-br from-violet-50 via-white to-cyan-50 border border-violet-200 rounded-3xl overflow-hidden shadow-pop"
            data-testid="tender-chat-panel"
        >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-violet-200 bg-white/60 backdrop-blur">
                <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-violet-600 to-cyan-500 text-white flex items-center justify-center shadow-pop">
                    <Sparkles size={16} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display font-black text-ink-950 text-base flex items-center gap-2">
                        Pregúntale a Claude 4.5 sobre este pliego
                    </div>
                    <div className="text-xs text-ink-500 font-medium">
                        Penalidades, plazos, criterios, personal a subrogar, costes mínimos… lo que necesites
                    </div>
                </div>
                {messages.length > 0 && (
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={exportPdf}
                            className="text-xs font-bold text-violet-700 hover:text-violet-900 flex items-center gap-1.5 rounded-xl px-2 py-1 hover:bg-violet-50"
                            data-testid="tender-chat-export-pdf"
                            title="Exportar conversación a PDF (brief para el equipo)"
                        >
                            <FileDown size={12} />
                            <span className="hidden md:inline">Exportar PDF</span>
                        </button>
                        <button
                            type="button"
                            onClick={clear}
                            className="text-xs font-bold text-ink-500 hover:text-red-600 flex items-center gap-1.5 rounded-xl px-2 py-1 hover:bg-red-50"
                            data-testid="tender-chat-clear"
                            title="Borrar conversación"
                        >
                            <Trash2 size={12} />
                            <span className="hidden md:inline">Borrar</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Messages */}
            <div className="max-h-[440px] overflow-y-auto px-5 py-4 space-y-4">
                {empty ? (
                    <div className="py-6 text-center" data-testid="tender-chat-empty">
                        <div className="inline-flex items-center gap-2 text-violet-700 text-sm font-bold mb-3">
                            <HelpCircle size={14} />
                            Empieza con una de estas preguntas
                        </div>
                        <div className="flex flex-wrap gap-2 justify-center">
                            {SUGGESTIONS.map((s, i) => (
                                <button
                                    key={`sugg-${i}`}
                                    type="button"
                                    onClick={() => send(s)}
                                    disabled={sending}
                                    className="text-xs font-medium bg-white border border-violet-200 text-ink-700 hover:bg-violet-50 hover:border-violet-400 rounded-full px-3 py-1.5 transition-colors disabled:opacity-50"
                                    data-testid={`tender-chat-suggestion-${i}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        <div className="text-[11px] text-ink-400 mt-4 font-medium">
                            O escribe tu pregunta abajo — la IA leerá el pliego completo y la analítica
                        </div>
                    </div>
                ) : (
                    messages.map((m, i) => (
                        <div
                            key={`m-${i}`}
                            className={`flex gap-3 ${m.role === "user" ? "" : "flex-row"}`}
                            data-testid={`tender-chat-msg-${m.role}-${i}`}
                        >
                            <div
                                className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
                                    m.role === "user"
                                        ? "bg-brand-50 text-brand-700 border border-brand-200"
                                        : "bg-gradient-to-br from-violet-600 to-cyan-500 text-white"
                                }`}
                            >
                                {m.role === "user" ? <UserIcon size={14} /> : <Bot size={14} />}
                            </div>
                            <div
                                className={`flex-1 min-w-0 px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-line break-words ${
                                    m.role === "user"
                                        ? "bg-brand-50 text-ink-900 border border-brand-100"
                                        : "bg-white text-ink-900 border border-violet-100 shadow-sm"
                                }`}
                            >
                                {m.content}
                            </div>
                        </div>
                    ))
                )}
                {sending && (
                    <div className="flex gap-3" data-testid="tender-chat-typing">
                        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-600 to-cyan-500 text-white flex items-center justify-center shrink-0">
                            <Bot size={14} />
                        </div>
                        <div className="px-4 py-3 rounded-2xl bg-white border border-violet-100 text-sm text-ink-500 italic flex items-center gap-2">
                            <Loader2 size={13} className="animate-spin" />
                            Claude está leyendo el pliego…
                        </div>
                    </div>
                )}
                <div ref={messagesEnd} />
            </div>

            {/* Input */}
            <div className="border-t border-violet-200 bg-white/80 backdrop-blur px-4 py-3">
                <div className="flex items-end gap-2">
                    <Textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={onKey}
                        placeholder="Ej: «¿Cuántos trabajadores hay que subrogar y cuál es el coste anual mínimo?» — Enter para enviar"
                        rows={2}
                        className="flex-1 resize-none bg-white border-violet-200 rounded-2xl text-sm focus-visible:ring-violet-500"
                        disabled={sending}
                        data-testid="tender-chat-input"
                    />
                    <Button
                        type="button"
                        onClick={() => send()}
                        disabled={sending || !input.trim()}
                        className="rounded-2xl h-[60px] px-5 bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-700 hover:to-cyan-600 text-white font-bold shadow-pop disabled:opacity-60"
                        data-testid="tender-chat-send"
                    >
                        {sending ? (
                            <Loader2 size={16} className="animate-spin" />
                        ) : (
                            <Send size={16} />
                        )}
                    </Button>
                </div>
                <div className="text-[11px] text-ink-400 mt-1.5 font-medium">
                    Claude Sonnet 4.5 · responde solo con datos del pliego (no inventa)
                </div>
            </div>
        </div>
    );
}

export default TenderChat;
